import { useState } from "react";
import { AlertTriangle, TrendingDown, Info, ChevronDown, ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import type { ProjectAlert, AlertSeverity } from "@/hooks/useProjectAlerts";

interface Props {
  alerts: ProjectAlert[];
  onNavigate?: (tab: string) => void;
}

const SEVERITY_CONFIG: Record<AlertSeverity, { icon: typeof AlertTriangle; border: string; bg: string; text: string; badge: string }> = {
  critical: {
    icon: AlertTriangle,
    border: "border-destructive/40",
    bg: "bg-destructive/5",
    text: "text-destructive",
    badge: "bg-destructive/15 text-destructive border-destructive/30",
  },
  warning: {
    icon: TrendingDown,
    border: "border-yellow-500/40",
    bg: "bg-yellow-500/5",
    text: "text-yellow-700 dark:text-yellow-400",
    badge: "bg-yellow-500/15 text-yellow-700 dark:text-yellow-400 border-yellow-500/30",
  },
  info: {
    icon: Info,
    border: "border-blue-500/40",
    bg: "bg-blue-500/5",
    text: "text-blue-700 dark:text-blue-400",
    badge: "bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/30",
  },
};

const MAX_VISIBLE = 3;

export const ProjectAlertsBanner = ({ alerts, onNavigate }: Props) => {
  const [expanded, setExpanded] = useState(false);

  if (!alerts.length) return null;

  const visible = expanded ? alerts : alerts.slice(0, MAX_VISIBLE);
  const overflow = alerts.length - MAX_VISIBLE;
  const worstSeverity = alerts[0]?.severity ?? "info"; // alerts are pre-sorted
  const config = SEVERITY_CONFIG[worstSeverity];

  return (
    <div className={`rounded-lg border ${config.border} ${config.bg} p-3 space-y-2`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <config.icon className={`h-4 w-4 ${config.text}`} />
          <span className={`text-sm font-semibold ${config.text}`}>
            {alerts.length} Financial Alert{alerts.length !== 1 ? "s" : ""}
          </span>
          {alerts.filter((a) => a.severity === "critical").length > 0 && (
            <Badge variant="outline" className={SEVERITY_CONFIG.critical.badge + " text-[10px]"}>
              {alerts.filter((a) => a.severity === "critical").length} critical
            </Badge>
          )}
          {alerts.filter((a) => a.severity === "warning").length > 0 && (
            <Badge variant="outline" className={SEVERITY_CONFIG.warning.badge + " text-[10px]"}>
              {alerts.filter((a) => a.severity === "warning").length} warning
            </Badge>
          )}
        </div>
      </div>

      <div className="space-y-1.5">
        {visible.map((alert) => {
          const ac = SEVERITY_CONFIG[alert.severity];
          return (
            <div
              key={alert.id}
              className="flex items-start justify-between gap-3 rounded-md border border-border/50 bg-background/60 px-3 py-2"
            >
              <div className="flex items-start gap-2 min-w-0">
                <ac.icon className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${ac.text}`} />
                <div className="min-w-0">
                  <span className="text-xs font-semibold">{alert.title}</span>
                  <p className="text-xs text-muted-foreground leading-relaxed">{alert.explanation}</p>
                </div>
              </div>
              {alert.linkTab && onNavigate && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-[10px] shrink-0"
                  onClick={() => onNavigate(alert.linkTab!)}
                >
                  View <ArrowRight className="h-3 w-3 ml-0.5" />
                </Button>
              )}
            </div>
          );
        })}
      </div>

      {overflow > 0 && (
        <Button
          variant="ghost"
          size="sm"
          className="h-6 text-xs w-full"
          onClick={() => setExpanded(!expanded)}
        >
          <ChevronDown className={`h-3 w-3 mr-1 transition-transform ${expanded ? "rotate-180" : ""}`} />
          {expanded ? "Show fewer" : `${overflow} more alert${overflow !== 1 ? "s" : ""}`}
        </Button>
      )}
    </div>
  );
};
